import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { getDialCode } from '@/constants/countries';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';

import type {
  IPluginDetailResponse,
  IUpdateMLFulfillRequest,
} from '@/providers/HttpProvider/plugins/MLFulfill/types';
import type { RootState } from '@/redux/store';
import type { MuiTelInputInfo } from 'mui-tel-input';

import { PLATFORM_NAME } from './constants';

interface UseMLFulfillConfigProps {
  pluginId: string;
  clientId: string;
  isOpen: boolean;
}

interface StoreInfo {
  storeEmail: string;
  storeName: string;
  storePhone: string;
  sellerId: string;
}

const normalizePhone = (phone: string, dialCode: string) => {
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  // Add a space between country code and national number so libphonenumber
  // doesn't greedily match a longer prefix (e.g. +228 Togo vs +52 Mexico).
  const digits = phone.replace(/\D/g, '');
  return `${dialCode} ${digits}`;
};

const useMLFulfillConfig = (props: UseMLFulfillConfigProps) => {
  const { pluginId, clientId, isOpen } = props;
  const { data: profile } = useSelector((state: RootState) => state.parcel.profile);
  const dialCode = getDialCode(profile?.country);

  const [storeInfo, setStoreInfo] = useState<StoreInfo>({
    storeEmail: '',
    storeName: '',
    storePhone: '',
    sellerId: '',
  });
  const [autoCreateOrder, setAutoCreateOrder] = useState(false);
  const [autoCreateLogisticModes, setAutoCreateLogisticModes] = useState<string[]>([]);
  const [skuSource, setSkuSource] = useState<string>('item_id');
  const [processOrdersWebhook, setProcessOrdersWebhook] = useState(false);
  const [me1AsTailorMade, setMe1AsTailorMade] = useState(false);
  const [isWebhookPrimary, setIsWebhookPrimary] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onChangeStoreInfo = (field: keyof StoreInfo, value: string) => {
    setStoreInfo((prev) => ({ ...prev, [field]: value }));
  };

  const onChangePhone = (value: string, info: MuiTelInputInfo) => {
    setStoreInfo((prev) => ({ ...prev, storePhone: value }));
    const digits = (info.nationalNumber ?? '').replace(/\D/g, '');
    setPhoneError(digits.length > 0 && digits.length < 7);
  };

  const onChangeAutoCreateOrder = (enabled: boolean) => {
    setAutoCreateOrder(enabled);
  };

  const onChangeLogisticModes = (modes: string[]) => {
    setAutoCreateLogisticModes(modes);
  };

  const onChangeSkuSource = (value: string) => {
    setSkuSource(value);
  };

  const onChangeIsWebhookPrimary = (isPrimary: boolean) => {
    setIsWebhookPrimary(isPrimary);
  };

  const loadConfig = useCallback(async () => {
    if (!clientId || !pluginId) return;
    setLoading(true);
    setPhoneError(false);
    try {
      const http = initializeHttpProvider();
      const res = await http.mlFulfill.getPluginDetails({ clientId, pluginId });

      if ('success' in res && res.success) {
        const { localConfig } = (res as IPluginDetailResponse).data;
        setStoreInfo({
          storeEmail: localConfig.storeEmail || '',
          storeName: localConfig.storeName || '',
          storePhone: normalizePhone(localConfig.storePhone || '', dialCode),
          sellerId: localConfig.sellerId || '',
        });
        setAutoCreateOrder(Boolean(localConfig.autoCreateOrder));
        setAutoCreateLogisticModes(
          Array.isArray(localConfig.autoCreateLogisticModes)
            ? localConfig.autoCreateLogisticModes
            : [],
        );
        setSkuSource(localConfig.skuSource || 'item_id');
        setProcessOrdersWebhook(Boolean(localConfig.processOrdersWebhook));
        setMe1AsTailorMade(
          Boolean(
            localConfig.me1AsTailorMade ??
            (localConfig as Record<string, unknown>)?.ME1AsTailorMade,
          ),
        );
        setIsWebhookPrimary(localConfig.webhookPlatform === PLATFORM_NAME);
      }
    } catch {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  }, [clientId, pluginId, dialCode]);

  const onSaveConfig = async () => {
    if (!clientId || !pluginId) {
      toast.error('Faltan datos requeridos');
      return false;
    }
    if (phoneError) {
      toast.error('El teléfono de la tienda no es válido');
      return false;
    }
    setSaving(true);
    try {
      const http = initializeHttpProvider();

      const requestData: IUpdateMLFulfillRequest = {
        local_config: {
          autoCreateOrder,
          useCollectAddress: false,
          collectAddress: {
            address: '',
            country: '',
            latitude: 0,
            longitude: 0,
            references: '',
            zipCode: '',
          },
          sellerId: storeInfo.sellerId,
          storeEmail: storeInfo.storeEmail,
          storeName: storeInfo.storeName,
          storePhone: storeInfo.storePhone,
          webhookPlatform: isWebhookPrimary ? PLATFORM_NAME : null,
          autoCreateLogisticModes,
          skuSource,
          processOrdersWebhook,
          me1AsTailorMade,
        },
      };

      const res = await http.mlFulfill.updatePlugin({ clientId, pluginId, data: requestData });

      if ('success' in res && res.success) {
        toast.success('Configuración guardada exitosamente');
        return true;
      }
      toast.error('Error al guardar configuración');
      return false;
    } catch {
      toast.error('Error al guardar configuración');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadConfig();
  }, [isOpen, pluginId, clientId, loadConfig]);

  return {
    storeInfo,
    onChangeStoreInfo,
    onChangePhone,
    phoneError,
    autoCreateOrder,
    onChangeAutoCreateOrder,
    autoCreateLogisticModes,
    onChangeLogisticModes,
    skuSource,
    onChangeSkuSource,
    isWebhookPrimary,
    onChangeIsWebhookPrimary,
    loading,
    saving,
    onSaveConfig,
  };
};

export default useMLFulfillConfig;
